/** What a list holds once the attacher is done with it. */
export type ListingBatchAttachment = {
  /** The companies the list was sent and accepted, in the order sent. */
  readonly attachedIds: readonly string[];
  /** The companies whose last attempt failed: the list does not hold them. */
  readonly failedIds: readonly string[];
};

export type ListingBatchAttacher = {
  /** Queues a company, sent with the next batch; a company already queued is ignored. */
  readonly add: (companyId: string) => void;
  /**
   * Resolves once every queued company was sent, with those the list holds
   * and those whose last attempt failed. Never rejects: the batches sent
   * before a failure stay attached, and the caller has to say so.
   */
  readonly finish: () => Promise<ListingBatchAttachment>;
};

/**
 * Attaches companies to a list while they are still being found, one batch in
 * flight at a time: what arrives during a request leaves with the next one. A
 * list filled this way holds every company found so far, so a page closed or
 * reloaded mid-way leaves it partial, never empty.
 *
 * A failed batch is kept and retried with the next one, then once more by
 * `finish`.
 */
export function createListingBatchAttacher(
  post: (companiesIds: string[]) => Promise<void>,
): ListingBatchAttacher {
  const seen = new Set<string>();
  const attached: string[] = [];
  let queued: string[] = [];
  let inFlight: Promise<void> | null = null;

  const send = async (): Promise<void> => {
    const batch = queued;
    queued = [];
    try {
      await post(batch);
      attached.push(...batch);
    } catch (error) {
      queued = [...batch, ...queued];
      throw error;
    }
  };

  const drain = (): void => {
    if (inFlight || queued.length === 0) return;
    inFlight = send().then(
      () => {
        inFlight = null;
        drain();
      },
      () => {
        // Retried by the next `add`, or by `finish` at the latest.
        inFlight = null;
      },
    );
  };

  return {
    add(companyId) {
      if (seen.has(companyId)) return;
      seen.add(companyId);
      queued.push(companyId);
      drain();
    },
    async finish() {
      while (inFlight) await inFlight;
      if (queued.length > 0) {
        try {
          await send();
        } catch {
          // Reported below: what is still queued is what the list lacks.
        }
      }
      return { attachedIds: [...attached], failedIds: [...queued] };
    },
  };
}
